import { Component, ElementRef, HostListener, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ApiService } from '../api/api.service';
import { AuthService } from '../auth/auth.service';
import { Company } from '../api/models';

interface SwitcherNode {
  company: Company;
  depth: number;
}

@Component({
  selector: 'app-company-switcher',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="switcher" [class.open]="open()">
      <button type="button" class="trigger" (click)="toggle($event)" [disabled]="switching() !== null">
        <span class="dot"></span>
        <span class="current">
          <span class="label">Company</span>
          <span class="name">{{ currentName() ?? 'No company' }}</span>
        </span>
        <span class="caret" [class.up]="open()">▾</span>
      </button>

      @if (open()) {
        <div class="menu" (click)="$event.stopPropagation()">
          <div class="search">
            <input type="text" placeholder="Search companies…" [(ngModel)]="query"
                   (ngModelChange)="onQueryChange($event)" #searchInput />
          </div>

          <div class="list">
            @if (loading()) {
              <div class="state">Loading…</div>
            } @else if (visible().length === 0) {
              <div class="state">No matches</div>
            } @else {
              @for (node of visible(); track node.company.id) {
                <button type="button" class="item"
                        [class.current]="node.company.id === currentId()"
                        [style.padding-left.px]="14 + node.depth * 16"
                        (click)="select(node.company.id)"
                        [disabled]="switching() === node.company.id">
                  @if (node.depth > 0) { <span class="branch">↳</span> }
                  <span class="item-name">{{ node.company.name }}</span>
                  @if (node.company.id === currentId()) {
                    <span class="check" aria-label="current">✓</span>
                  } @else if (switching() === node.company.id) {
                    <span class="spinner">…</span>
                  }
                </button>
              }
            }
          </div>

          <div class="footer">
            <a routerLink="/companies/new" (click)="close()">+ Add company</a>
            <a routerLink="/companies" (click)="close()">Manage…</a>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    :host { display: inline-block; position: relative; }

    .trigger {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 220px;
      padding: 6px 10px;
      background: var(--color-surface);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-sm);
      color: var(--color-text);
      cursor: pointer;
      font: inherit;
      text-align: left;
      transition: background 0.12s, border-color 0.12s;
    }
    .trigger:hover:not(:disabled) { background: var(--color-sidebar); border-color: var(--color-border-strong); }
    .trigger:disabled { opacity: 0.7; cursor: not-allowed; }

    .dot { width: 8px; height: 8px; border-radius: 50%; background: var(--color-brand); flex-shrink: 0; }
    .current { flex: 1; min-width: 0; display: flex; flex-direction: column; line-height: 1.15; }
    .current .label { font-size: 10px; color: var(--color-text-subtle); text-transform: uppercase; letter-spacing: 0.08em; }
    .current .name { font-size: var(--fs-sm); color: var(--color-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; }

    .caret { color: var(--color-text-subtle); font-size: 10px; transition: transform 0.15s; }
    .caret.up { transform: rotate(180deg); }

    .menu {
      position: absolute;
      top: calc(100% + 6px);
      right: 0;
      min-width: 280px;
      background: var(--color-surface);
      color: var(--color-text);
      border: 1px solid var(--color-border);
      border-radius: var(--radius-md);
      box-shadow: var(--shadow-lg);
      z-index: 50;
      overflow: hidden;
      animation: slideDown 0.12s ease-out;
    }
    @keyframes slideDown { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }

    .search { padding: 8px 8px 6px; border-bottom: 1px solid var(--color-border-soft); }
    .search input {
      width: 100%;
      padding: 6px 10px;
      border: 1px solid var(--color-border);
      border-radius: var(--radius-sm);
      font-size: var(--fs-sm);
      box-sizing: border-box;
      background: var(--color-surface);
    }
    .search input:focus { outline: none; border-color: var(--color-brand); box-shadow: 0 0 0 3px var(--color-brand-soft); }

    .list { max-height: 320px; overflow-y: auto; padding: 4px 0; }
    .state { padding: 12px 16px; color: var(--color-text-muted); font-size: var(--fs-sm); text-align: center; }

    .item {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 7px 12px;
      background: none;
      border: 0;
      cursor: pointer;
      font: inherit;
      font-size: var(--fs-sm);
      color: var(--color-text);
      text-align: left;
    }
    .item:hover:not(:disabled) { background: var(--color-brand-softer); }
    .item:disabled { opacity: 0.6; cursor: not-allowed; }
    .item.current { color: var(--color-brand-hover); font-weight: 500; background: var(--color-brand-softer); }
    .item .branch { color: var(--color-text-subtle); }
    .item .item-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .item .check { color: var(--color-brand); font-weight: 600; }

    .footer {
      border-top: 1px solid var(--color-border-soft);
      padding: 4px;
      display: flex;
      gap: 4px;
    }
    .footer a {
      flex: 1;
      text-align: center;
      padding: 7px;
      color: var(--color-brand);
      text-decoration: none;
      font-size: var(--fs-sm);
      border-radius: var(--radius-sm);
    }
    .footer a:hover { background: var(--color-brand-softer); text-decoration: none; }
  `]
})
export class CompanySwitcherComponent {
  private api = inject(ApiService);
  private auth = inject(AuthService);
  private host = inject(ElementRef<HTMLElement>);

  private all = signal<Company[]>([]);
  private loaded = false;

  loading = signal(false);
  open = signal(false);
  switching = signal<number | null>(null);
  query = '';
  private queryLower = signal('');

  currentId = this.auth.currentCompanyId;
  currentName = this.auth.currentCompanyName;

  private tree = computed<SwitcherNode[]>(() => {
    const list = this.all();
    const byParent = new Map<number | null, Company[]>();
    for (const c of list) {
      const key = c.parentCompanyId ?? null;
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key)!.push(c);
    }
    const visibleIds = new Set(list.map(c => c.id));
    const nodes: SwitcherNode[] = [];
    const walk = (parent: number | null, depth: number) => {
      for (const c of byParent.get(parent) ?? []) {
        nodes.push({ company: c, depth });
        walk(c.id, depth + 1);
      }
    };
    walk(null, 0);
    for (const c of list) {
      if (c.parentCompanyId !== null && !visibleIds.has(c.parentCompanyId)) {
        nodes.push({ company: c, depth: 0 });
        walk(c.id, 1);
      }
    }
    return nodes;
  });

  visible = computed<SwitcherNode[]>(() => {
    const q = this.queryLower();
    if (!q) return this.tree();
    return this.tree().filter(n => n.company.name.toLowerCase().includes(q));
  });

  toggle(event: Event): void {
    event.stopPropagation();
    const willOpen = !this.open();
    this.open.set(willOpen);
    if (willOpen && !this.loaded) this.refresh();
  }

  close(): void { this.open.set(false); }

  onQueryChange(value: string): void {
    this.queryLower.set((value ?? '').trim().toLowerCase());
  }

  refresh(): void {
    this.loading.set(true);
    this.api.getMyCompanies().subscribe({
      next: list => {
        this.all.set(list);
        this.loaded = true;
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  select(id: number): void {
    if (id === this.currentId()) { this.close(); return; }
    this.switching.set(id);
    this.auth.switchCompany(id).subscribe({
      next: () => { window.location.assign('/dashboard'); },
      error: () => this.switching.set(null)
    });
  }

  @HostListener('document:click', ['$event'])
  onDocClick(event: Event): void {
    if (!this.host.nativeElement.contains(event.target as Node)) this.open.set(false);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void { this.open.set(false); }
}
